import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateLocationLabelsPdf, LabelSize } from '../../utils/generateLocationLabelsPdf';
import { useTranslation } from 'react-i18next';
import { useLocations } from '../../hooks/useLocations';
import { Location } from '../../models/Location';
import { ErrorState, LoadingState } from '../feedback';
import LocationsHeader from './LocationsHeader';
import LocationsModals, { LocationsModalsRef } from './LocationsModals';
import LocationsView from './LocationsView';

const createHeaderActions = (handleCreateLocation: () => void, t: (key: string) => string) => [
  {
    id: 'create',
    label: t('button.create'),
    onClick: handleCreateLocation,
    variant: 'primary' as const,
  },
];

export const Main = () => {
  const { t } = useTranslation('locationsModule');
  const { canAccess, hasPermission } = usePermissions();
  const modalsRef = useRef<LocationsModalsRef>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());

  // 🎯 Verificar permisos de ubicaciones
  const canCreate = canAccess(['tenant', 'tenant_3pl'], ['locations.create']);
  const canEdit = canAccess(['tenant', 'tenant_3pl'], ['locations.edit']);
  const canDelete = canAccess(['tenant', 'tenant_3pl'], ['locations.delete']);
  const canManageStock = hasPermission('locations.manage-stock');

  // 🎯 Hook para manejar query parameters (para URL sync)
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<any>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
  })); // 🎯 Tipo flexible para AG Grid y VirtualTable

  // 🎯 Configuración de navegación con traducción correcta
  const breadcrumbs = useMemo(() => [{ label: t('title'), path: '/locations' }], [t]);

  // 🎯 Sincronizar estados locales con cambios en URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
    };
    setSorting((prev) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // 🎯 Procesar filterConditions a campos específicos
  const processedFilters = useMemo(() => {
    const type_ids: string[] = [];
    const product_ids: string[] = [];
    const product_team_ids: string[] = [];

    filterConditions.forEach((condition) => {
      if (condition.column === 'locationType' && condition.action === 'equals') {
        type_ids.push(condition.value);
      } else if (condition.column === 'productName' && condition.action === 'equals') {
        product_ids.push(condition.value);
      } else if (condition.column === 'product_team_ids' && condition.action === 'in') {
        const ids = Array.isArray(condition.value) ? condition.value : [condition.value];
        product_team_ids.push(...ids.map(String));
      }
    });

    return {
      ...(type_ids.length > 0 && {
        type_ids: type_ids.length === 1 ? type_ids[0] : type_ids,
      }),
      ...(product_ids.length > 0 && {
        product_ids: product_ids.length === 1 ? product_ids[0] : product_ids,
      }),
      ...(product_team_ids.length > 0 && {
        product_team_ids: product_team_ids.join(','),
      }),
      ...(sorting &&
        sorting.direction && {
          sortBy: sorting.field,
          sortDirection: sorting.direction,
        }),
    };
  }, [filterConditions, sorting]);

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Hook con scroll infinito + filtros específicos
  const { locations, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useLocations({
      searchTerm: searchTerm,
      ...processedFilters,
    });

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Handlers para el header
  const handleCreateLocation = useCallback(() => {
    modalsRef.current?.openCreateModal();
  }, []);

  // 🎯 Handlers para sincronizar con URL - memoizados para evitar bucles
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      // Solo actualizar URL si el valor es diferente al actual
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      // Solo actualizar URL si los filtros son diferentes
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  const handleSortingChange = useCallback(
    (newSorting: any) => {
      setSorting(newSorting);

      if (newSorting && newSorting.field && newSorting.direction) {
        // Solo actualizar URL si el sorting es diferente al actual
        if (queryParams.sortBy !== newSorting.field || queryParams.sortDirection !== newSorting.direction) {
          updateQueryParams({
            sortBy: newSorting.field,
            sortDirection: newSorting.direction,
          });
        }
      } else {
        // Resetear a valores por defecto si no hay sorting
        const defaultSorting = { field: 'id', direction: 'desc' };
        setSorting(defaultSorting);

        if (queryParams.sortBy !== defaultSorting.field || queryParams.sortDirection !== defaultSorting.direction) {
          updateQueryParams({
            sortBy: defaultSorting.field,
            sortDirection: defaultSorting.direction,
          });
        }
      }
    },
    [updateQueryParams, queryParams.sortBy, queryParams.sortDirection],
  );

  // 🎯 Handlers para las ubicaciones - ZERO re-renders
  const handleLocationEdit = useCallback((location: Location) => {
    console.log('Editar ubicación:', location);
    modalsRef.current?.openEditModal(location);
  }, []);

  const handleLocationDelete = useCallback((location: Location) => {
    console.log('Eliminar ubicación:', location);
    modalsRef.current?.openDeleteModal(location);
  }, []);

  const handleLocationAdjust = useCallback((location: Location) => {
    console.log('Ajustar ubicación:', location);
    modalsRef.current?.openAdjustModal(location);
  }, []);

  const handleLocationPrint = useCallback(async (location: Location, size: LabelSize) => {
    try {
      await generateLocationLabelsPdf([location], size);
    } catch (error) {
      console.error('Error generando PDF:', error);
    }
  }, []);

  const handleSelectionChanged = useCallback((selectedIds: Set<number>) => {
    setSelectedLocationIds(selectedIds);
  }, []);

  const handleClearLocationSelection = useCallback(() => {
    setSelectedLocationIds(new Set());
  }, []);

  const handleBulkLabelsPrintSuccess = useCallback(() => {
    setSelectedLocationIds(new Set());
  }, []);

  const handlePrintSelectedLabels = useCallback(() => {
    const selected = locations.filter((l) => selectedLocationIds.has(l.id));
    if (selected.length === 0) return;
    modalsRef.current?.openBulkLabelModal(selected);
  }, [locations, selectedLocationIds]);

  const handleLocationToggleLock = useCallback((location: Location) => {
    const action = location.is_locked ? 'unlock' : 'lock';
    console.log(`${action} ubicación:`, location);
    if (location.is_locked) {
      modalsRef.current?.openUnlockModal(location);
    } else {
      modalsRef.current?.openLockModal(location);
    }
  }, []);

  // 🎯 Handler para refetch
  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  // 🎯 Configuración de navegación optimizada
  // Solo mostrar botón crear en header si hay ubicaciones (total > 0) y tiene permiso
  const headerActions = useMemo(
    () => (total > 0 && canCreate ? createHeaderActions(handleCreateLocation, t) : []),
    [handleCreateLocation, t, total, canCreate],
  );

  const hasActiveFilters = filterConditions.length > 0;

  // Usuarios 3PL ven filtro y columna de cliente del producto
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error as Error} onRetry={handleRefetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* 🎯 Header - Filtros y búsqueda */}
      <LocationsHeader onSearch={handleSearch} onFilterChange={handleFilterChange} hasTeamData={hasTeamData} />

      {/* 🎯 Vista - Tabla/Cards con infinite scroll */}
      <LocationsView
        locations={locations}
        error={error}
        onLocationEdit={canEdit ? handleLocationEdit : undefined}
        onLocationDelete={canDelete ? handleLocationDelete : undefined}
        onLocationAdjust={canManageStock ? handleLocationAdjust : undefined}
        onLocationPrint={handleLocationPrint}
        onLocationToggleLock={handleLocationToggleLock}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        total={total}
        isLoading={isLoading}
        hasActiveFilters={hasActiveFilters}
        hasSearchTerm={Boolean(searchTerm)}
        onCreateLocation={canCreate ? handleCreateLocation : undefined}
        selectedLocationIds={selectedLocationIds}
        onSelectionChanged={handleSelectionChanged}
        onClearLocationSelection={handleClearLocationSelection}
        onPrintSelectedLabels={handlePrintSelectedLabels}
        hasTeamData={hasTeamData}
      />

      {/* 🎯 Modales - Gestión centralizada */}
      <LocationsModals ref={modalsRef} onRefetch={handleRefetch} onBulkLabelsGenerated={handleBulkLabelsPrintSuccess} />
    </div>
  );
};
